import React, { createContext, useContext } from 'react';
import useLocalStorage from 'helpers/localStorage/useLocalStorage';
import { PageSpecificKeys } from 'helpers/localStorage/constants';
import { BOESlideoutFormValues } from 'views/DealDashboard/BOE/components/SlideoutForm/types';
import { ProFormaQuickStartValues } from '../ProFormaQuickStart/types';

/* eslint-disable no-unused-vars */
export enum SignUpStage {
  BOE_CALCULATOR = 'BOE_CALCULATOR',
  PRO_FORMA_QUICK_START = 'PRO_FORMA_QUICK_START',
  PRO_FORMA_DASHBOARD = 'PRO_FORMA_DASHBOARD',
  ACCOUNT_CREATED = 'ACCOUNT_CREATED',
  ERROR = 'ERROR',
}

export enum SignUpStageErrorType {
    RESOURCE_CREATION = 'RESOURCE_CREATION',
}

export type SignUpStageData = {
  [SignUpStage.BOE_CALCULATOR]?: BOESlideoutFormValues;
  [SignUpStage.PRO_FORMA_QUICK_START]?: ProFormaQuickStartValues;
  [SignUpStage.PRO_FORMA_DASHBOARD]?: { firstName: string; lastName: string; email: string }; // TODO: Add actual interface definition
  [SignUpStage.ACCOUNT_CREATED]?: { submitted: boolean };
  [SignUpStage.ERROR]?: { type: SignUpStageErrorType; message: string };
};

interface SignUpContextType {
    currentStage: SignUpStage;
    setCurrentStage: (stage: SignUpStage) => void;
    stageData: SignUpStageData;
    updateStageData: (data: SignUpStageData[SignUpStage], stage?: SignUpStage) => void;
    currentStageData: SignUpStageData[SignUpStage];
  }

const SignUpContext = createContext<SignUpContextType | undefined>(undefined);

export const SignUpProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentStage, setCurrentStage] = useLocalStorage<SignUpStage>(
        PageSpecificKeys.SignUpStage,
        SignUpStage.BOE_CALCULATOR,
    );
    const [stageData, setStageData] = useLocalStorage<SignUpStageData>(
        PageSpecificKeys.SignUpData,
        {},
    );
    const currentStageData = stageData[currentStage];

    const updateStageData = (data: SignUpStageData[SignUpStage], stage?: SignUpStage) => {
        const targetStage = stage || currentStage;
        setStageData(prev => ({
            ...prev,
            [targetStage]: { ...data },
        }));
    };

    return (
        <SignUpContext.Provider value={{
            currentStage,
            setCurrentStage,
            stageData,
            updateStageData,
            currentStageData,
        }}
        >
            {children}
        </SignUpContext.Provider>
    );
};

export const useSignUpContext = () => {
    const context = useContext(SignUpContext);
    if (!context) throw new Error('useSignUpContext must be used inside SignUpProvider');
    return context;
};
